import ControlledTable from "@/components/controlled-table";
import { useTableServer } from "@/hooks/useTableServer";
import PromotionRepository from "@/utilities/repositories/Promotion";
import { useCallback, useMemo, useState, useEffect } from "react";
import useApi from "@/hooks/useApi";
import { IResponseData } from "@/utilities/types/requests";
import { IPromotion, EPromotionStatus } from "@/utilities/types/Promotion";
import toast from "react-hot-toast";
import { getColumns } from "./columns";
import FilterElement from "./filter-element";

export default function PromotionTable() {
  const { request: deletePromotion } = useApi<IResponseData<IPromotion>>({
    request: PromotionRepository.deletePromotion,
    enableToast: true,
  });

  const onHeaderCellClick = (value: string) => ({
    onClick: () => {
      handleSort(value);
    },
  });

  const {
    isLoading,
    tableData,
    currentPage,
    totalItems,
    handlePaginate,
    pageSize,
    handleChangePageSize,
    searchTerm,
    handleSearch,
    sortConfig,
    handleSort,
    filters,
    updateFilter,
    handleReset,
    refresh,
  } = useTableServer({
    server: {
      request: PromotionRepository.getPromotionList,
    },
    searchBy: (searchTerm) => {
      const s = searchTerm || undefined;
      return {
        search: s,
      };
    },
  });

  const onDeleteItem = useCallback(
    async (id: string) => {
      await deletePromotion(id);
      handleReset(); // Recall API
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [handleReset]
  );

  const onUpdatePromotion = useCallback(
    async (id: string, data: Partial<IPromotion>) => {
      try {
        await PromotionRepository.updatePromotion(id, data);
        handleReset(); // Recall API
        toast.success("Promotion updated successfully");
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Update failed";
        toast.error(errorMessage);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [handleReset]
  );

  const columns = useMemo(
    () =>
      getColumns({
        data: tableData,
        sortConfig,
        onHeaderCellClick,
        onDeleteItem,
        handleReset,
        refresh,
        onUpdatePromotion,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      onHeaderCellClick,
      sortConfig.key,
      sortConfig.direction,
      onDeleteItem,
      tableData,
      onUpdatePromotion,
    ]
  );

  return (
    <div className="mt-4">
      <FilterElement
        onSearch={handleSearch}
        searchTerm={searchTerm}
        status={filters?.status}
        updateFilter={updateFilter}
        variant="promotions"
      />
      <ControlledTable
        variant="modern"
        data={tableData}
        isLoading={isLoading}
        showLoadingText={true}
        // @ts-expect-error - The ControlledTable component expects a different column type
        columns={columns}
        paginatorOptions={{
          pageSize,
          setPageSize: handleChangePageSize,
          total: totalItems,
          current: currentPage,
          onChange: (page: number) => handlePaginate(page),
        }}
        className="rounded-md border border-muted text-sm shadow-sm [&_.rc-table-placeholder_.rc-table-expanded-row-fixed>div]:h-60 [&_.rc-table-placeholder_.rc-table-expanded-row-fixed>div]:justify-center [&_.rc-table-row:last-child_td.rc-table-cell]:border-b-0 [&_thead.rc-table-thead]:border-t-0"
      />
    </div>
  );
}
